/**
 * annotations.ts — the single source of truth for "model projection → visual
 * cue on the graph."
 *
 * The model (a Python agent over WS, or a replay bundle) emits `Annotations`
 * on each tick: bottlenecks, anomaly regions, relationship clusters, and the
 * decisions the agent made. This module turns those into the per-node /
 * per-edge cues the React Flow components already know how to render:
 *
 *   bottleneck → node shrinks + red ring + out-edges slow their dash
 *   anomaly    → node amber ring + (future) hull around the subgraph
 *   cluster    → node stroke recoloured by cluster id
 *   decision   → side-panel entry (text), surfaced through `decisionFeed`
 *
 * Keeping the mapping here (not inline in GraphCanvas) means it's grep-able,
 * unit-testable, and the same logic could drive a future accessibility mode
 * (e.g. announce decisions to a screen reader) without touching the canvas.
 *
 * Hysteresis: a node must be flagged a bottleneck for N consecutive ticks
 * before the cue fires. This is the plan's "overlays firing on noise" failure
 * mode — a single noisy tick shouldn't redraw the graph. N=2 by default.
 */
import type { Annotations, DecisionRef, NodeId, ScenarioBundle, TickPayload } from '../engine/types';
import { OVERLAY_COLORS } from '../graph/palette';

/** Per-node visual cues derived from this tick's annotations + history. */
export interface NodeCue {
  bottleneck: boolean;
  anomalous: boolean;
  /** Recolour the node stroke with this colour when set (cluster id). Null
   *  means "use the default group colour". */
  clusterColor: string | null;
  /** When true, the node is part of an anomaly region that should be hull'd
   *  (Phase 4 future; canvas may render a bounding box around these). */
  inAnomalyRegion: boolean;
  /** Label for the anomaly region, if any. */
  anomalyLabel: string | null;
}

export interface EdgeCue {
  /** Slow the dash animation because the source node is a bottleneck. */
  slowdown: boolean;
}

export interface DecisionEntry {
  step: number;
  node?: NodeId;
  nodeLabel?: string;
  action: string;
  rationale?: string;
  /** Authoring agent's identity key (AgentConfig.id or the derived
   *  node:/edge:/global: attachment id). Absent = the model itself. */
  agent?: string;
  refs?: DecisionRef[];
}

/** One row of the agent activity log (the DecisionsModal's table). Folds a
 *  tick's decisions + agentLogs into one flat, source-agnostic shape — the
 *  live buffer (useSim) and the replay aggregation both produce these. */
export interface AgentActivityEntry {
  step: number;
  /** null = authored by the model itself, not a named agent. */
  agent: string | null;
  kind: 'decision' | 'log';
  level?: 'debug' | 'info' | 'warn';
  node?: NodeId;
  /** The row's headline text: a decision's action / a log's message. */
  message: string;
  rationale?: string;
  refs?: DecisionRef[];
  data?: unknown;
}

/** Fold one tick's decisions + agentLogs into activity entries (decisions
 *  first, then logs). Pure — the caller owns retention/windowing. */
export function toActivityEntries(tick: TickPayload): AgentActivityEntry[] {
  const ann: Annotations = tick.annotations ?? {};
  const out: AgentActivityEntry[] = [];
  for (const d of ann.decisions ?? []) {
    out.push({
      step: tick.step,
      agent: d.agent ?? null,
      kind: 'decision',
      node: d.node,
      message: d.action,
      rationale: d.rationale,
      refs: d.refs,
    });
  }
  for (const l of ann.agentLogs ?? []) {
    out.push({
      step: tick.step,
      agent: l.agent ?? null,
      kind: 'log',
      level: l.level,
      message: l.message,
      data: l.data,
    });
  }
  return out;
}

export interface OverlayState {
  nodeCues: Record<NodeId, NodeCue>;
  edgeCues: Record<string, EdgeCue>;
  /** Decisions accumulated this tick (and, optionally, retained history). */
  decisions: DecisionEntry[];
}

/** Build the per-node / per-edge cue maps for one tick, given the previous
 *  hysteresis counters. Returns the new OverlayState AND the updated counter
 *  maps so the caller can thread them into the next tick. Pure; no React. */
export function deriveOverlays(
  tick: TickPayload,
  graph: { nodes: { id: NodeId }[]; edges: { id: string; source: NodeId }[] },
  prevCounts: Map<NodeId, number>,
  opts: { hysteresis?: number; retainDecisions?: number; prevDecisions?: DecisionEntry[] } = {},
): { state: OverlayState; counts: Map<NodeId, number> } {
  const hysteresis = opts.hysteresis ?? 2;
  const retain = opts.retainDecisions ?? 8;
  const ann: Annotations = tick.annotations ?? {};

  // ─── Bottleneck counts with hysteresis ─────────────────────────────────
  const bnSet = new Set(ann.bottlenecks ?? []);
  const counts = new Map<NodeId, number>();
  const activeBn = new Set<NodeId>();
  for (const n of graph.nodes) {
    const prev = prevCounts.get(n.id) ?? 0;
    const next = bnSet.has(n.id) ? prev + 1 : 0;
    counts.set(n.id, next);
    if (next >= hysteresis) activeBn.add(n.id);
  }

  // ─── Anomaly regions ───────────────────────────────────────────────────
  const anomalyColor = new Map<NodeId, { label: string | null }>();
  for (const a of ann.anomalies ?? []) {
    for (const n of a.nodes) anomalyColor.set(n, { label: a.label ?? null });
  }

  // ─── Clusters (last-write-wins on overlap; non-overlapping in practice) ─
  const clusterOf = new Map<NodeId, string>();
  for (const c of ann.clusters ?? []) {
    for (const n of c.nodes) clusterOf.set(n, c.color);
  }

  // ─── Assemble per-node cues ────────────────────────────────────────────
  const nodeCues: Record<NodeId, NodeCue> = {};
  for (const n of graph.nodes) {
    const anom = anomalyColor.get(n.id) ?? null;
    nodeCues[n.id] = {
      bottleneck: activeBn.has(n.id),
      anomalous: anom !== null,
      clusterColor: clusterOf.get(n.id) ?? null,
      inAnomalyRegion: anom !== null,
      anomalyLabel: anom?.label ?? null,
    };
  }

  // ─── Per-edge cues: slowdown iff the source is an active bottleneck ────
  const edgeCues: Record<string, EdgeCue> = {};
  for (const e of graph.edges) {
    edgeCues[e.id] = { slowdown: activeBn.has(e.source) };
  }

  // ─── Decisions feed (retain the last N across ticks) ───────────────────
  const fresh: DecisionEntry[] = (ann.decisions ?? []).map((d) => ({
    step: tick.step,
    node: d.node,
    action: d.action,
    rationale: d.rationale,
    agent: d.agent,
    refs: d.refs,
  }));
  const prev = opts.prevDecisions ?? [];
  const decisions = [...prev, ...fresh].slice(-retain);

  return { state: { nodeCues, edgeCues, decisions }, counts };
}

/** Palette export for components that need the raw colours (e.g. the cluster
 *  ring or the decision feed's accent stripe). Kept here so the overlay
 *  module is the single import surface for "model projection colours". */
export { OVERLAY_COLORS };

// ─── Agent identity resolution (the activity log's sidebar labels) ──────────

/** Every agent configured in the bundle, keyed by its identity key
 *  (`AgentConfig.id` when minted, else the derived attachment id). Pure. */
export function collectAgentConfigs(
  bundle: ScenarioBundle,
): { id: string; name: string | null; host: string | null }[] {
  const out: { id: string; name: string | null; host: string | null }[] = [];
  for (const cfg of bundle.agents ?? []) {
    out.push({ id: cfg.id ?? `global:${cfg.target}`, name: cfg.name ?? null, host: null });
  }
  for (const n of bundle.graph.nodes) {
    if (n.agentConfig) {
      out.push({ id: n.agentConfig.id ?? `node:${n.id}`, name: n.agentConfig.name ?? null, host: n.id });
    }
  }
  for (const e of bundle.graph.edges) {
    if (e.agentConfig) {
      out.push({ id: e.agentConfig.id ?? `edge:${e.id}`, name: e.agentConfig.name ?? null, host: e.id });
    }
  }
  return out;
}

/** Resolve an agent identity key to a human display name: the config's own
 *  `name` wins; else a derived id resolves to its host's label
 *  (`node:buf` → the node's label, `edge:e1` → the edge's source→target);
 *  a minted id with no name falls back to the host label or the raw id.
 *  Pure — the modal builds a callback over the current bundle. */
export function agentDisplayName(id: string, bundle: ScenarioBundle | null): string {
  const known = bundle ? collectAgentConfigs(bundle).find((a) => a.id === id) : undefined;
  if (known?.name) return known.name;
  const sep = id.indexOf(':');
  const kind = sep > 0 ? id.slice(0, sep) : '';
  const key = sep > 0 ? id.slice(sep + 1) : id;
  if (bundle) {
    if (kind === 'node') {
      const n = bundle.graph.nodes.find((x) => x.id === key);
      if (n) return n.label;
    } else if (kind === 'edge') {
      const e = bundle.graph.edges.find((x) => x.id === key);
      if (e) {
        const s = bundle.graph.nodes.find((x) => x.id === e.source)?.label ?? e.source;
        const t = bundle.graph.nodes.find((x) => x.id === e.target)?.label ?? e.target;
        return `${s} → ${t}`;
      }
    }
    if (known?.host) {
      // A minted id with no name: the host label is friendlier than the uuid.
      const hostNode = bundle.graph.nodes.find((x) => x.id === known.host);
      if (hostNode) return hostNode.label;
      const hostEdge = bundle.graph.edges.find((x) => x.id === known.host);
      if (hostEdge) {
        const s = bundle.graph.nodes.find((x) => x.id === hostEdge.source)?.label ?? hostEdge.source;
        const t = bundle.graph.nodes.find((x) => x.id === hostEdge.target)?.label ?? hostEdge.target;
        return `${s} → ${t}`;
      }
    }
  }
  if (kind === 'global') return key;
  return id;
}
